/**
 * Pure billing rules for a conversation in progress (§9): the first
 * exchange costs the length the player chose (a word or a sit), every
 * exchange after that costs a word. Pulled out of App.tsx so the one rule
 * that decides money-on-the-clock is one place, and testable without a
 * React harness.
 */

import type { NpcId } from '@sim/index.ts';

export interface Talking {
  readonly npc: NpcId;
  /** What an uncharged exchange costs: the length the player chose. */
  readonly minutes: number;
  readonly charged: boolean;
}

/** Opening a conversation fresh: nothing has been billed yet. */
export function startTalking(npc: NpcId, minutes: number): Talking {
  return { npc, minutes, charged: false };
}

/**
 * Reconnecting to a conversation that survived a reload. The sim's own
 * transcript is the only record of whether a real exchange already
 * happened — length 1 is just the opener, nobody has spoken back yet.
 *
 * The length the player originally chose (word or sit) is not recoverable
 * here: it was never sim state, only this module's, and reloading lost it.
 * §9 makes time the only cost of anything, and the player didn't choose a
 * long sit here — they reloaded a page. `wordMinutes` is used as the
 * uncharged rate: overbilling for a choice never made is worse than
 * underbilling.
 */
export function resumeTalking(npc: NpcId, transcriptLength: number, wordMinutes: number): Talking {
  return { npc, minutes: wordMinutes, charged: transcriptLength > 1 };
}

/** What the exchange about to be sent costs. */
export function billedMinutes(talking: Talking, wordMinutes: number): number {
  return talking.charged ? wordMinutes : talking.minutes;
}

/** After a successful exchange that didn't end the scene: from here, a word each. */
export function afterExchange(talking: Talking): Talking {
  return { ...talking, charged: true };
}
